/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';

import { token } from '@atlaskit/tokens';

const fileHoveredColor = token('color.skeleton'); // Neutral200A hex
const dividerColor = token('color.border'); // Neutral300A hex
const colorGreen = token('color.text.accent.green');
const colorRed = token('color.text.accent.red');
const colorBlue = token('color.text.accent.blue');
const colorYellow = token('color.text.accent.yellow'); // Yellow800 hex
const colorDisabled = token('color.text.disabled');
const colorTextSubtlest = token('color.text.subtlest');
const backgroundColorGreen = token('color.background.accent.green.subtlest');
const backgroundColorRed = token('color.background.accent.red.subtlest');
const backgroundColorBlue = token('color.background.accent.blue.subtlest');
const backgroundColorYellow = token('color.background.accent.yellow.subtlest');

const space0 = token('space.0');
const space50 = token('space.050');
const space75 = token('space.075');
const space100 = token('space.100');
const space200 = token('space.200');

export const TooltipWrapper = styled.span<{ isReviewed?: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ isReviewed }) =>
    isReviewed ? `color: ${colorDisabled};` : `color: ${colorTextSubtlest};`}
`;

export const FileTreeWrapper = styled.ul({
  listStyle: 'none',
  paddingLeft: 0,
  marginTop: '8px',
  marginBottom: '16px',
  marginRight: '8px',
});

export const Directory = styled.li({
  userSelect: 'none',
});

export const DirectoryContent = styled.ul({
  listStyle: 'none',
  paddingLeft: 0,
  borderLeft: `1px solid ${dividerColor}`,
  marginLeft: space200,
  marginBottom: space50,
});

export const DirectoryHeader = styled.button<{
  isCollapsed: boolean;
}>`
  border: 0;
  background: none;
  display: flex;
  align-items: center;
  font: ${token('font.body')};
  cursor: pointer;
  color: inherit;
  width: 100%;
  margin-left: 5px;
  margin-bottom: ${({ isCollapsed }) => `${isCollapsed ? space0 : space50}`};
  padding: 0;

  &:hover,
  &:focus {
    text-decoration: none;
    color: inherit;
  }

  > div:last-of-type {
    display: flex;
    flex-shrink: 0;
    overflow: hidden;
  }
`;

export const DirectoryFolder = styled.div({
  width: space200,
  marginRight: space50,
});

/* remove this disable once https://github.com/YozhikM/stylelint-a11y/issues/38 is addressed */
/* stylelint-disable a11y/media-prefers-reduced-motion */
export const File = styled.a<{
  isActive: boolean;
  isHidden: boolean;
}>(({ isActive, isHidden }) => ({
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  padding: `2px ${space50} 0 12px`,
  marginBottom: '2px',
  backgroundColor: isActive ? backgroundColorBlue : 'transparent',
  // eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
  borderRadius: token('border.radius', '3px'),
  font: token('font.body'),
  cursor: 'pointer',
  transition: 'all 0.2s ease-out',
  textDecoration: 'none',
  height: '20px',
  opacity: isHidden ? 0.5 : 1,

  '&:hover, &:focus': {
    backgroundColor: fileHoveredColor,
    color: 'inherit',
    transition: 'all 0.2s ease-in',
    textDecoration: 'none',
  },

  '@media screen and (prefers-reduced-motion: reduce)': {
    /* https://css-tricks.com/revisiting-prefers-reduced-motion-the-reduced-motion-media-query/ */
    transitionDuration: '0.001ms',
    '&:hover, &:focus': {
      transitionDuration: '0.001ms',
    },
  },

  '> div:last-of-type': {
    display: 'flex',
    flexShrink: 0,
    overflow: 'hidden',
  },
}));
/* stylelint-enable */

export const FileNotes = styled.div({
  marginLeft: 'auto',
  height: '100%',
  alignItems: 'center',
});

export const FileName = styled.span({
  marginLeft: space50,
});

export const CommentsWrapper = styled.span({
  display: 'flex',
  alignItems: 'center',
  marginLeft: space100,
  marginRight: space100,
  height: '100%',

  /* !! Hacky overrides for AK Icons that don't support our needs !! */
  "& > span[role='presentation'], & svg": {
    verticalAlign: 'unset',
    height: '12px',
    width: '12px',
  },
});

export const CommentsNumber = styled.span({
  marginTop: 0,
  marginLeft: space50,
  fontWeight: token('font.weight.regular'),
  '& span': {
    color: token('color.text'),
  },
});

export const LinesAdded = styled.span<{
  cutCorners: boolean;
  isColorBlindMode: boolean;
}>`
  margin-top: 0;

  ${({ cutCorners, isColorBlindMode }) => {
    let baseStyling = ``;
    baseStyling = isColorBlindMode
      ? `background-color: ${backgroundColorBlue};
        & span {
           color: ${colorBlue};
        }`
      : `background-color: ${backgroundColorGreen};
        & span {
          color: ${colorGreen};
        }`;

    baseStyling = cutCorners
      ? `${baseStyling} border-radius: ${space75} ${space0} ${space0} ${space75};`
      : `${baseStyling} border-radius: ${space75};`;

    baseStyling = `${baseStyling} padding: 3px 4px;`;
    return baseStyling;
  }}
`;

export const LinesRemoved = styled.span<{
  cutCorners: boolean;
  isColorBlindMode: boolean;
}>`
  margin-top: 0;

  ${({ cutCorners, isColorBlindMode }) => {
    let baseStyling = ``;
    baseStyling = isColorBlindMode
      ? `background-color: ${backgroundColorYellow};
      & span {
           color: ${colorYellow};}`
      : `background-color: ${backgroundColorRed};
      & span {
           color: ${colorRed};}`;

    baseStyling = cutCorners
      ? `${baseStyling} border-radius: ${space0} ${space75} ${space75} ${space0};`
      : `${baseStyling} border-radius: ${space75};`;

    baseStyling = `${baseStyling} padding: 3px 4px;`;
    return baseStyling;
  }}
`;
